/**
 * Reload Debugger Utility
 * Tracks and logs all potential causes of page reloads
 * Helps identify what triggers unexpected page refreshes
 */

class ReloadDebugger {
  constructor() {
    this.reloadEvents = []
    this.isTracking = false
    this.startTime = Date.now()
    this.urlCheckInterval = null
    
    // Track various reload triggers
    this.setupTracking()
  }

  setupTracking() {
    if (typeof window === 'undefined') return

    // Track window.location changes
    this.trackLocationChanges()
    
    // Track window.reload calls
    this.trackReloadCalls()
    
    // Track beforeunload events
    this.trackBeforeUnload()
    
    // Track visibility changes
    this.trackVisibilityChanges()
    
    // Track navigation events
    this.trackNavigation()
    
    // Track Supabase auth state changes
    this.trackSupabaseAuth()
    
    // Track error boundaries
    this.trackErrors()
    
    // Track React Router navigation
    this.trackRouterNavigation()
  }

  logEvent(type, details = {}) {
    const event = {
      type,
      timestamp: Date.now(),
      timeSinceStart: Date.now() - this.startTime,
      details,
      stack: new Error().stack,
    }
    
    this.reloadEvents.push(event)
    
    // Log to console with color coding
    const colors = {
      location: '#ff6b6b',
      reload: '#ee5a6f',
      beforeunload: '#feca57',
      visibility: '#48dbfb',
      navigation: '#0abde3',
      auth: '#5f27cd',
      error: '#ee5a6f',
      router: '#00d2d3',
    }
    
    console.log(
      `%c[Reload Debug] ${type}`,
      `color: ${colors[type] || '#000'}; font-weight: bold;`,
      details
    )
    
    // Store in sessionStorage for persistence across reloads
    try {
      const existing = JSON.parse(sessionStorage.getItem('reloadDebugEvents') || '[]')
      existing.push(event)
      // Keep only last 50 events
      const recent = existing.slice(-50)
      sessionStorage.setItem('reloadDebugEvents', JSON.stringify(recent))
    } catch (e) {
      console.warn('Could not save reload debug event:', e)
    }
  }

  trackLocationChanges() {
    // Note: window.location methods are read-only, so we can't override them directly
    // Instead, we'll track navigation through other means (beforeunload, popstate, etc.)
    // and monitor URL changes
    
    let currentUrl = window.location.href
    
    // Monitor URL changes using MutationObserver on document
    // and periodic checks
    const checkUrlChange = () => {
      const newUrl = window.location.href
      if (newUrl !== currentUrl) {
        this.logEvent('location', {
          method: 'change',
          oldUrl: currentUrl,
          newUrl: newUrl,
        })
        currentUrl = newUrl
      }
    }
    
    // Check for URL changes periodically (every 100ms)
    this.urlCheckInterval = setInterval(checkUrlChange, 100)
    
    // Also track when location methods are called (if possible)
    // We'll wrap them in a try-catch since they're read-only
    try {
      // Try to create a proxy - this won't work for location, but we'll catch errors
      const locationProxy = new Proxy(window.location, {
        get(target, prop) {
          if (prop === 'assign' || prop === 'replace') {
            return function(...args) {
              reloadDebugger.logEvent('location', {
                method: prop,
                url: args[0],
                currentUrl: window.location.href,
              })
              return target[prop].apply(target, args)
            }
          }
          return target[prop]
        }
      })
      
      // Note: We can't actually replace window.location with a proxy
      // So we'll just track through other means
    } catch (e) {
      // Expected - location is read-only, we'll track through events instead
      console.debug('[Reload Debug] Cannot proxy window.location (read-only), using event tracking instead')
    }
  }

  trackReloadCalls() {
    // Note: window.location.reload is also read-only
    // We'll track reloads through beforeunload events
    try {
      const originalReload = window.location.reload
      
      // Try to override reload (will fail if read-only)
      Object.defineProperty(window.location, 'reload', {
        value: function(...args) {
          reloadDebugger.logEvent('reload', {
            method: 'reload',
            args,
            currentUrl: window.location.href,
          })
          return originalReload.apply(this, args)
        },
        writable: true,
        configurable: true,
      })
    } catch (e) {
      // Expected - reload is read-only
      // We'll track through beforeunload and visibility events instead
      console.debug('[Reload Debug] Cannot override window.location.reload (read-only), tracking through events')
    }
  }

  trackBeforeUnload() {
    window.addEventListener('beforeunload', (e) => {
      this.logEvent('beforeunload', {
        reason: 'Page unload',
        url: window.location.href,
        returnValue: e.returnValue,
      })
    })
  }

  trackVisibilityChanges() {
    document.addEventListener('visibilitychange', () => {
      this.logEvent('visibility', {
        state: document.visibilityState,
        url: window.location.href,
        timestamp: Date.now(),
      })
    })
  }

  trackNavigation() {
    // Track popstate (back/forward)
    window.addEventListener('popstate', (e) => {
      this.logEvent('navigation', {
        type: 'popstate',
        state: e.state,
        url: window.location.href,
      })
    })

    // Track hashchange
    window.addEventListener('hashchange', (e) => {
      this.logEvent('navigation', {
        type: 'hashchange',
        oldURL: e.oldURL,
        newURL: e.newURL,
      })
    })
  }

  trackSupabaseAuth() {
    // This will be called from App.jsx or wherever Supabase is initialized
    // We'll provide a method to track auth state changes
  }

  trackErrors() {
    // Track unhandled errors
    window.addEventListener('error', (e) => {
      this.logEvent('error', {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        error: e.error?.toString(),
      })
    })

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (e) => {
      this.logEvent('error', {
        type: 'unhandledrejection',
        reason: e.reason?.toString(),
        promise: e.promise,
      })
    })
  }

  trackRouterNavigation() {
    // This will be called from App.jsx to track React Router navigation
  }

  // Method to track Supabase auth state changes
  trackAuthStateChange(event, session) {
    // Get previous session from storage to detect if this is a restoration
    let previousSessionData = null
    try {
      const previousSession = sessionStorage.getItem('lastAuthSession')
      if (previousSession) {
        previousSessionData = JSON.parse(previousSession)
      }
    } catch (e) {
      // Ignore parse errors
    }
    
    const currentUserId = session?.user?.id
    const previousUserId = previousSessionData?.userId
    
    // This is a session restoration if:
    // 1. We have a previous session stored
    // 2. The user ID matches
    // 3. This is a SIGNED_IN event (not initial load)
    // 4. The previous event was not SIGNED_OUT (meaning user was already signed in)
    const isSessionRestoration = previousSessionData && 
      currentUserId && 
      previousUserId === currentUserId &&
      event === 'SIGNED_IN' &&
      previousSessionData.event !== 'SIGNED_OUT' &&
      previousSessionData.event !== 'INITIAL_LOAD'
    
    // Store current session for next comparison
    if (session?.user?.id) {
      sessionStorage.setItem('lastAuthSession', JSON.stringify({
        userId: session.user.id,
        timestamp: Date.now(),
        event: event,
      }))
    } else {
      sessionStorage.removeItem('lastAuthSession')
    }
    
    // Determine if this is problematic
    const isProblematic = event === 'SIGNED_IN' && isSessionRestoration && 
      previousSessionData?.event !== 'SIGNED_OUT'
    
    this.logEvent('auth', {
      event,
      hasSession: !!session,
      userId: currentUserId,
      url: window.location.href,
      isSessionRestoration,
      previousUserId,
      currentUserId,
      previousSession: previousSessionData ? 'exists' : 'none',
      warning: isProblematic 
        ? '⚠️ SIGNED_IN fired for existing session - may cause unnecessary reload' 
        : null,
      recommendation: isProblematic
        ? 'Consider ignoring this SIGNED_IN event in auth listeners'
        : null,
    })
  }

  // Method to track React Router navigation
  trackRouterNavigation(pathname, search, state) {
    this.logEvent('router', {
      pathname,
      search,
      state,
      url: window.location.href,
    })
  }

  // Get all events
  getEvents() {
    return this.reloadEvents
  }

  // Get events from sessionStorage (persists across reloads)
  getPersistedEvents() {
    try {
      return JSON.parse(sessionStorage.getItem('reloadDebugEvents') || '[]')
    } catch (e) {
      return []
    }
  }

  // Print summary report
  printReport() {
    const events = this.getPersistedEvents()
    const grouped = events.reduce((acc, event) => {
      acc[event.type] = (acc[event.type] || 0) + 1
      return acc
    }, {})

    // Analyze potential reload causes
    const potentialIssues = []
    const authEvents = events.filter(e => e.type === 'auth')
    const duplicateSignIns = authEvents.filter((e, idx) => {
      if (e.details?.event === 'SIGNED_IN' && e.details?.isSessionRestoration) {
        return true
      }
      return false
    })
    
    if (duplicateSignIns.length > 0) {
      potentialIssues.push({
        issue: 'Duplicate SIGNED_IN events detected',
        count: duplicateSignIns.length,
        description: 'SIGNED_IN events fired for existing sessions (session restoration). This may cause unnecessary reloads.',
        recommendation: 'Check OrganizationContext and other auth listeners to ignore duplicate SIGNED_IN events.',
      })
    }

    const visibilityChanges = events.filter(e => e.type === 'visibility')
    const rapidVisibilityChanges = visibilityChanges.filter((e, idx) => {
      if (idx === 0) return false
      const prev = visibilityChanges[idx - 1]
      const timeDiff = e.timestamp - prev.timestamp
      return timeDiff < 2000 && prev.details?.state === 'hidden' && e.details?.state === 'visible'
    })
    
    if (rapidVisibilityChanges.length > 0) {
      potentialIssues.push({
        issue: 'Rapid tab switching detected',
        count: rapidVisibilityChanges.length,
        description: 'Multiple visibility changes in quick succession. This may trigger Supabase token refreshes.',
        recommendation: 'Ensure auth listeners ignore TOKEN_REFRESHED events.',
      })
    }

    console.group('%c[Reload Debug Report]', 'font-size: 16px; font-weight: bold; color: #5f27cd;')
    console.log('Total events tracked:', events.length)
    console.table(grouped)
    
    if (potentialIssues.length > 0) {
      console.group('%c⚠️ Potential Issues Found', 'color: #feca57; font-weight: bold;')
      potentialIssues.forEach((issue, idx) => {
        console.group(`%c${idx + 1}. ${issue.issue}`, 'color: #ee5a6f; font-weight: bold;')
        console.log('Count:', issue.count)
        console.log('Description:', issue.description)
        console.log('Recommendation:', issue.recommendation)
        console.groupEnd()
      })
      console.groupEnd()
    }
    
    console.log('Recent events:', events.slice(-10))
    console.groupEnd()

    return {
      total: events.length,
      grouped,
      recent: events.slice(-10),
      potentialIssues,
    }
  }

  // Clear all events
  clear() {
    this.reloadEvents = []
    sessionStorage.removeItem('reloadDebugEvents')
    if (this.urlCheckInterval) {
      clearInterval(this.urlCheckInterval)
      this.urlCheckInterval = null
    }
    console.log('%c[Reload Debug] Events cleared', 'color: #5f27cd; font-weight: bold;')
  }
  
  // Cleanup method
  destroy() {
    this.clear()
  }
}

// Create singleton instance
const reloadDebugger = new ReloadDebugger()

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.reloadDebugger = reloadDebugger
  
  // Add helper commands
  window.debugReloads = () => reloadDebugger.printReport()
  window.clearReloadDebug = () => reloadDebugger.clear()
  
  console.log(
    '%c[Reload Debug] Enabled',
    'color: #5f27cd; font-weight: bold; font-size: 14px;'
  )
  console.log(
    '%cUse window.debugReloads() to see reload events',
    'color: #5f27cd;'
  )
}

export default reloadDebugger

